import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Store } from "lucide-react";
import { createMiqCollection, updateCurrentUser } from "wasp/client/operations";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { useTranslation } from "../i18n/hooks";

type AddStoreDialogProps = {
  open: boolean;
  onOpenChange: (arg: boolean) => void;
};

const AddStoreDialog = ({ open, onOpenChange }: AddStoreDialogProps) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { t } = useTranslation();
  const [storeName, setStoreName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Generate a placeholder store name suggestion
  const suggestedName = currentUser ? `${currentUser?.email?.split('@')[0]}'s Store` : "My New Store";

  const handleAddStore = async () => {
    if (!currentUser) {
      setError(t("storeDialog.user_not_found"));
      return;
    }

    if (!storeName.trim()) {
      setError(t("storeDialog.enter_store_name"));
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const col = await createMiqCollection({
        name: storeName.trim(),
      });

      if (col) {
        await updateCurrentUser({
          id: currentUser.id,
          currentCollectionId: col.id,
        });
        // Show success briefly before reloading
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (err) {
      setError(t("storeDialog.creation_failed"));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden rounded-lg">
        <DialogHeader className="px-6 pt-6 pb-4 bg-gradient-to-r from-purple-50 to-blue-50">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-md">
              <Store className="h-5 w-5 text-blue-600" />
            </div>
            <DialogTitle className="text-xl">{t("storeDialog.create_new_store")}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-6 py-4">
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="text-sm text-gray-700 leading-relaxed">
                <p>{t("storeDialog.multiple_stores_description")}</p>
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="store-name" className="text-sm font-medium">
                {t("storeDialog.store_name")}
              </Label>
              <Input
                id="store-name"
                value={storeName}
                onChange={(e) => setStoreName(e.target.value)}
                placeholder={suggestedName}
                className="w-full"
                autoFocus
                autoComplete="off"
              />
              <p className="text-xs text-gray-500">
                {t("storeDialog.rename_later")}
              </p>
            </div>

            <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg flex items-start gap-2">
              <div className="min-w-4 pt-0.5">
                <div className="w-2 h-2 rounded-full bg-purple-400" />
              </div>
              <p className="text-xs text-purple-700">
                {t("storeDialog.switch_stores")}
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="py-2">
                <AlertDescription className="text-xs">{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 bg-gray-50 border-t">
          <div className="flex justify-between w-full gap-3">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full"
              disabled={isSubmitting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleAddStore}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("storeDialog.creating") : t("storeDialog.create_store")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddStoreDialog;